import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight, Mail } from "lucide-react";

export default function ForgotPassword() {
    const [email, setEmail] = useState("");
    const [done, setDone] = useState(false);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        setLoading(true);
        try {
            await api.post("/auth/forgot-password", { email });
            setDone(true);
        } catch (e) {
            setErr(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-white">
            <div className="flex flex-col p-8 sm:p-12 lg:p-16">
                <Link to="/" className="flex items-center gap-2.5 mb-12 w-fit" data-testid="forgot-logo-link">
                    <div className="h-8 w-8 bg-brand text-white grid place-items-center font-heading font-bold tracking-tight rounded-sm">A</div>
                    <div className="leading-tight">
                        <div className="font-heading font-bold text-slate-900 text-base">Allied Pod</div>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-mono">Barbados</div>
                    </div>
                </Link>
                <div className="flex-1 flex flex-col justify-center max-w-md w-full mx-auto">
                    <p className="text-[11px] uppercase tracking-[0.2em] font-mono text-slate-500 mb-3">Account recovery</p>
                    <h1 className="font-heading text-4xl tracking-tight font-bold text-slate-900">Reset your password.</h1>
                    {!done ? (
                        <>
                            <p className="text-sm text-slate-600 mt-3 leading-relaxed">
                                Enter your account email. Since automated email is disabled for this workspace,
                                an admin will share your reset link directly (WhatsApp, SMS or in person).
                            </p>
                            <form onSubmit={submit} className="mt-10 space-y-5">
                                <div>
                                    <Label htmlFor="email" className="text-xs uppercase tracking-[0.16em] font-mono text-slate-600">Email</Label>
                                    <Input
                                        id="email"
                                        type="email"
                                        required
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        data-testid="forgot-email-input"
                                        className="mt-1.5 h-11 bg-slate-50 border-slate-200 rounded-md focus-visible:ring-1 focus-visible:ring-brand focus-visible:border-brand"
                                        placeholder="you@company.com"
                                    />
                                </div>
                                {err && <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-sm">{err}</div>}
                                <Button
                                    type="submit"
                                    disabled={loading}
                                    data-testid="forgot-submit-btn"
                                    className="w-full h-11 bg-brand hover:bg-brand-hover text-white rounded-md group"
                                >
                                    {loading ? "Submitting…" : (
                                        <>
                                            Request reset link
                                            <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-0.5 transition-transform" strokeWidth={2} />
                                        </>
                                    )}
                                </Button>
                            </form>
                        </>
                    ) : (
                        <div className="mt-8 p-6 bg-emerald-50 border border-emerald-200 rounded-md" data-testid="forgot-success">
                            <div className="flex items-start gap-3">
                                <div className="h-9 w-9 bg-emerald-100 text-emerald-700 grid place-items-center rounded-sm shrink-0">
                                    <Mail className="h-4 w-4" strokeWidth={1.75} />
                                </div>
                                <div>
                                    <p className="font-heading font-semibold text-slate-900">Request received.</p>
                                    <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">
                                        If an account exists for <span className="font-mono">{email}</span>, an admin can share your reset link directly.
                                        Reach out to <span className="font-mono text-slate-900">alliedconsulting.global@gmail.com</span> if you don't hear back within 24 hours.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                    <p className="mt-8 text-sm text-slate-600">
                        Remember it after all?{" "}
                        <Link to="/login" data-testid="forgot-back-to-login" className="text-brand font-medium hover:underline underline-offset-4">
                            Back to sign in →
                        </Link>
                    </p>
                </div>
            </div>
            <div className="hidden lg:block relative overflow-hidden">
                <img
                    src="https://images.unsplash.com/photo-1760611656615-db3fad24a314?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBvZmZpY2UlMjBwb2R8ZW58MHx8fHwxNzc4MDExOTUwfDA&ixlib=rb-4.1.0&q=85"
                    alt="Modern office space"
                    className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-slate-900/20 to-transparent" />
            </div>
        </div>
    );
}
